import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import {
  ArrowLeft,
  Calendar,
  Users,
  BarChart3,
  Archive,
  Edit,
  Kanban,
  ListTodo,
  Milestone,
  GanttChartSquare,
} from 'lucide-react'
import { useState } from 'react'

import { ProjectForm } from '@/components/pm/project/project-form'
import { useConfirmDialog } from '@/hooks'
import {
  useProject,
  useProjectStats,
  useArchiveProject,
  type ProjectStatus,
} from '@/hooks'
import { cn } from '@/lib/utils'

/**
 * 格式化完成率显示
 * API 返回的 completionRate 可能是小数 (0-1) 或百分比 (0-100)
 * 此函数确保输出始终在 0-100% 范围内
 */
function displayCompletionRate(rate: number): string {
  // 如果值大于 1，假设已经是百分比形式
  const percentage = rate > 1 ? rate : rate * 100
  // 确保在 0-100 范围内
  const clampedPercentage = Math.min(100, Math.max(0, percentage))
  return `${clampedPercentage.toFixed(1)}%`
}

/**
 * 项目详情页面
 */
export const Route = createFileRoute('/_authenticated/pm/projects/$projectId/')({
  component: ProjectDetailPage,
})

const STATUS_LABELS: Record<ProjectStatus, string> = {
  planning: '规划中',
  active: '进行中',
  completed: '已完成',
  archived: '已归档',
}

const STATUS_COLORS: Record<ProjectStatus, string> = {
  planning: 'bg-blue-100 text-blue-800',
  active: 'bg-green-100 text-green-800',
  completed: 'bg-gray-100 text-gray-800',
  archived: 'bg-yellow-100 text-yellow-800',
}

function ProjectDetailPage() {
  const params = Route.useParams()
  const projectId = params.projectId
  const navigate = useNavigate()
  const [showEditForm, setShowEditForm] = useState(false)

  const { data: project, isLoading, error } = useProject(projectId)
  const { data: stats } = useProjectStats(projectId)
  const archiveMutation = useArchiveProject()
  const { confirmWithPreset, ConfirmDialog, setLoading } = useConfirmDialog()

  const handleArchive = async () => {
    const confirmed = await confirmWithPreset(
      'archive',
      '确定要归档此项目吗？归档后项目将不可编辑。'
    )
    if (confirmed) {
      setLoading(true)
      try {
        await archiveMutation.mutateAsync(projectId)
      } finally {
        setLoading(false)
      }
    }
  }

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-muted-foreground">加载中...</div>
      </div>
    )
  }

  if (error || !project) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4">
        <div className="text-destructive">项目不存在或加载失败</div>
        <Link
          to="/pm/projects"
          className="text-sm text-primary hover:underline"
        >
          返回项目列表
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* 返回和标题 */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => void navigate({ to: '/pm/projects' })}
          className="rounded-md p-2 hover:bg-muted"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">{project.code}</span>
            <span
              className={cn(
                'rounded-full px-2 py-0.5 text-xs font-medium',
                STATUS_COLORS[project.status]
              )}
            >
              {STATUS_LABELS[project.status]}
            </span>
          </div>
          <h1 className="text-2xl font-bold tracking-tight">{project.name}</h1>
        </div>
        <div className="flex items-center gap-2">
          {project.status !== 'archived' && (
            <>
              <button
                onClick={() => setShowEditForm(true)}
                className="inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium hover:bg-muted"
              >
                <Edit className="h-4 w-4" />
                编辑
              </button>
              {project.status === 'completed' && (
                <button
                  onClick={() => void handleArchive()}
                  disabled={archiveMutation.isPending}
                  className="inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50"
                >
                  <Archive className="h-4 w-4" />
                  归档
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* 项目描述 */}
      {project.description && (
        <p className="text-muted-foreground">{project.description}</p>
      )}

      {/* 日期信息 */}
      <div className="flex items-center gap-6 text-sm">
        {project.startDate && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Calendar className="h-4 w-4" />
            <span>开始: {new Date(project.startDate).toLocaleDateString('zh-CN')}</span>
          </div>
        )}
        {project.endDate && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Calendar className="h-4 w-4" />
            <span>截止: {new Date(project.endDate).toLocaleDateString('zh-CN')}</span>
          </div>
        )}
      </div>

      {/* 快捷导航 */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <Link
          to="/pm/projects/$projectId/board"
          params={{ projectId }}
          className="flex items-center gap-3 rounded-lg border bg-card p-4 transition-shadow hover:shadow-md"
        >
          <div className="rounded-md bg-primary/10 p-2">
            <Kanban className="h-5 w-5 text-primary" />
          </div>
          <div>
            <p className="font-medium">看板</p>
            <p className="text-sm text-muted-foreground">任务看板视图</p>
          </div>
        </Link>

        <Link
          to="/pm/projects/$projectId/gantt"
          params={{ projectId }}
          className="flex items-center gap-3 rounded-lg border bg-card p-4 transition-shadow hover:shadow-md"
        >
          <div className="rounded-md bg-orange-500/10 p-2">
            <GanttChartSquare className="h-5 w-5 text-orange-500" />
          </div>
          <div>
            <p className="font-medium">甘特图</p>
            <p className="text-sm text-muted-foreground">时间线视图</p>
          </div>
        </Link>

        <Link
          to="/pm/issues"
          search={{ projectId }}
          className="flex items-center gap-3 rounded-lg border bg-card p-4 transition-shadow hover:shadow-md"
        >
          <div className="rounded-md bg-blue-500/10 p-2">
            <ListTodo className="h-5 w-5 text-blue-500" />
          </div>
          <div>
            <p className="font-medium">任务列表</p>
            <p className="text-sm text-muted-foreground">查看所有任务</p>
          </div>
        </Link>

        <Link
          to="/pm/projects/$projectId/members"
          params={{ projectId }}
          className="flex items-center gap-3 rounded-lg border bg-card p-4 transition-shadow hover:shadow-md"
        >
          <div className="rounded-md bg-green-500/10 p-2">
            <Users className="h-5 w-5 text-green-500" />
          </div>
          <div>
            <p className="font-medium">项目成员</p>
            <p className="text-sm text-muted-foreground">查看项目成员</p>
          </div>
        </Link>

        <Link
          to="/pm/projects/$projectId/milestones"
          params={{ projectId }}
          className="flex items-center gap-3 rounded-lg border bg-card p-4 transition-shadow hover:shadow-md"
        >
          <div className="rounded-md bg-purple-500/10 p-2">
            <Milestone className="h-5 w-5 text-purple-500" />
          </div>
          <div>
            <p className="font-medium">里程碑</p>
            <p className="text-sm text-muted-foreground">项目里程碑</p>
          </div>
        </Link>
      </div>

      {/* 统计数据 */}
      {stats && (
        <div className="space-y-4">
          <h2 className="flex items-center gap-2 text-lg font-semibold">
            <BarChart3 className="h-5 w-5" />
            项目统计
          </h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-lg border bg-card p-4">
              <p className="text-sm text-muted-foreground">总任务数</p>
              <p className="mt-1 text-2xl font-bold">{stats.totalIssues}</p>
            </div>
            <div className="rounded-lg border bg-card p-4">
              <p className="text-sm text-muted-foreground">已完成</p>
              <p className="mt-1 text-2xl font-bold text-green-600">
                {stats.completedIssues}
              </p>
            </div>
            <div className="rounded-lg border bg-card p-4">
              <p className="text-sm text-muted-foreground">完成率</p>
              <p className="mt-1 text-2xl font-bold">
                {displayCompletionRate(stats.completionRate)}
              </p>
            </div>
            <div className="rounded-lg border bg-card p-4">
              <p className="text-sm text-muted-foreground">逾期任务</p>
              <p className={cn(
                'mt-1 text-2xl font-bold',
                stats.overdueIssues > 0 ? 'text-red-600' : ''
              )}>
                {stats.overdueIssues}
              </p>
            </div>
          </div>

          {/* 工时对比 */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-lg border bg-card p-4">
              <p className="text-sm text-muted-foreground">预估工时</p>
              <p className="mt-1 text-2xl font-bold">{stats.totalEstimatedHours}h</p>
            </div>
            <div className="rounded-lg border bg-card p-4">
              <p className="text-sm text-muted-foreground">实际工时</p>
              <p className="mt-1 text-2xl font-bold">{stats.totalActualHours}h</p>
            </div>
          </div>

          {/* 里程碑进度 */}
          {stats.milestoneProgress.length > 0 && (
            <div className="rounded-lg border bg-card p-4">
              <h3 className="mb-4 font-medium">里程碑进度</h3>
              <div className="space-y-3">
                {stats.milestoneProgress.map((milestone) => (
                  <div key={milestone.id}>
                    <div className="flex items-center justify-between text-sm">
                      <span>{milestone.name}</span>
                      <span className="text-muted-foreground">
                        {milestone.completedIssues}/{milestone.totalIssues}
                      </span>
                    </div>
                    <div className="mt-1 h-2 overflow-hidden rounded-full bg-muted">
                      <div
                        className="h-full bg-primary transition-all"
                        style={{ width: `${milestone.progress}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* 编辑表单弹窗 */}
      {showEditForm && (
        <ProjectForm
          project={project}
          onClose={() => setShowEditForm(false)}
          onSuccess={() => setShowEditForm(false)}
        />
      )}
      <ConfirmDialog />
    </div>
  )
}
